import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
    Image,
    Package,
    BarChart3,
    Settings,
    Loader2,
    Sparkles
} from 'lucide-react';
import './HomePage.css';

interface AppInfo {
    id: string;
    name: string;
    description: string;
    icon: string;
    route: string;
    color: string;
    order: number;
    isActive: boolean;
}

// Default apps if Firestore collection is empty
const defaultApps: AppInfo[] = [
    {
        id: 'image-generator',
        name: 'Générateur d\'Images AI',
        description: 'Générez des images produits avec l\'intelligence artificielle',
        icon: 'image',
        route: '/apps/image-generator',
        color: '#8B5CF6',
        order: 1,
        isActive: true
    }
];

// Icon mapping
const iconMap: Record<string, React.ComponentType<any>> = {
    'image': Image,
    'package': Package,
    'bar-chart': BarChart3,
    'settings': Settings,
    'sparkles': Sparkles,
};

export function HomePage() {
    const navigate = useNavigate();
    const { userProfile, isAdmin } = useAuth();
    const [apps, setApps] = useState<AppInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadApps();
    }, []);

    const loadApps = async () => {
        try {
            const appsRef = collection(db, 'apps');
            const q = query(appsRef, orderBy('order', 'asc'));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                // Use default apps if collection is empty
                setApps(defaultApps);
            } else {
                const loadedApps = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as AppInfo));
                setApps(loadedApps.filter(app => app.isActive));
            }
        } catch (error) {
            console.error('Error loading apps:', error);
            setApps(defaultApps);
        } finally {
            setIsLoading(false);
        }
    };

    // Filter apps based on user permissions
    const visibleApps = apps.filter(app => {
        if (isAdmin) return true; // Admins see all apps
        const allowedApps = userProfile?.allowedApps || [];
        // If no allowedApps defined, show all active apps (backward compatibility)
        if (allowedApps.length === 0) return true;
        return allowedApps.includes(app.id);
    });

    const handleAppClick = (app: AppInfo) => {
        navigate(app.route);
    };

    if (isLoading) {
        return (
            <div className="home-loading">
                <Loader2 className="spinner" size={48} />
            </div>
        );
    }

    return (
        <div className="home-page">
            <header className="home-header">
                <div className="home-header-content">
                    <div className="home-logo">
                        <Sparkles size={32} />
                    </div>
                    <h1>Bienvenue, {userProfile?.displayName || 'Utilisateur'}</h1>
                    <p>Sélectionnez une application pour commencer</p>
                </div>
            </header>

            <main className="home-main">
                <div className="apps-grid">
                    {visibleApps.map(app => {
                        const IconComponent = iconMap[app.icon] || Sparkles;
                        return (
                            <div
                                key={app.id}
                                className="app-card glass"
                                onClick={() => handleAppClick(app)}
                                style={{ '--app-color': app.color } as React.CSSProperties}
                            >
                                <div className="app-icon" style={{ backgroundColor: app.color }}>
                                    <IconComponent size={32} />
                                </div>
                                <div className="app-info">
                                    <h3>{app.name}</h3>
                                    <p>{app.description}</p>
                                </div>
                                <div className="app-arrow">→</div>
                            </div>
                        );
                    })}
                </div>

                {visibleApps.length === 0 && (
                    <div className="no-apps glass">
                        <Package size={48} />
                        <h2>Aucune application disponible</h2>
                        <p>Contactez un administrateur pour obtenir l'accès aux applications.</p>
                    </div>
                )}
            </main>

            {isAdmin && (
                <div className="admin-quick-access glass">
                    <button onClick={() => navigate('/admin/users')}>
                        <Settings size={18} />
                        Administration
                    </button>
                </div>
            )}
        </div>
    );
}
